import type { OnboardingStep } from './onboardingStep';

/**
 * Pure routing decision for App.tsx's "/" route (Wave-1 C1,
 * docs/DEMO-WALKTHROUGH-issues.md #51). Kept framework-free (no React, no
 * react-router) so the fix for "route a 0-corpus authed user INTO
 * onboarding, not to Home" is directly unit-testable without mounting
 * <App> — this repo has no jsdom/React-Testing-Library setup (every other
 * *.tsx decision in this app is tested this same way: extract the pure
 * logic into its own module, see onboardingStep.ts/jobBuckets.ts/
 * dragRehome.ts/stageMove.ts).
 *
 * THE BUG THIS FIXES: AppShell renders the onboarding overlay as a purely
 * VISUAL, absolutely-positioned cover over the SAME <Routes> children that
 * always mount Home at "/" (see AppShell.tsx's own doc comment) — so a
 * brand-new, 0-corpus user's Home rendered underneath the overlay
 * regardless of onboarding step, relying entirely on CSS layering to hide
 * it. DB-verified live evidence: a net-new account (0 roles/0 jobs/0 apps)
 * reached the fully-rendered placeholder Home instead of being routed into
 * onboarding.
 *
 * THE FIX: gate the ROUTE, not just the visual layer. shouldRenderHome
 * returns false exactly when a user is both (a) still mid-onboarding
 * (step !== 'done') AND (b) genuinely has no real corpus yet
 * (hasRealCorpus === false) — a returning user who already has data always
 * gets Home immediately, matching today's behavior; only the true
 * 0-corpus/mid-onboarding case is gated. Deliberately narrow: does NOT
 * touch the onboarding flow's own intentional hand-offs into REAL /refine
 * (step 3) or /profiles (step 4) — those pages mount for real underneath
 * the overlay at those specific steps by design (the diagram's own node
 * 3/4 behavior) and already render their own honest cold/loading states.
 */
export function shouldRenderHome(step: OnboardingStep, hasRealCorpus: boolean): boolean {
  const stillOnboarding = step !== 'done';
  return !(stillOnboarding && !hasRealCorpus);
}

/**
 * E7 FRONT DOOR EXTENSION (LANE E7-PORT-THROUGHLINE, 2026-07-17) — "extend
 * homeRouteGate's existing semantics to include the new onboarded_at/
 * thinness logic served by the API" (docs/diagrams/c4-e7-front-door-
 * 2026-07-17.html). The 7 profile-facts cards (lib/frontDoorCards.ts,
 * components/FrontDoorCards.tsx) are a NEW phase composed AHEAD of the
 * corpus-discovery arc this file already gates — "/" must stay hidden while
 * EITHER phase is showing, not just the existing one.
 *
 * Deliberately a NEW function, not a changed signature on shouldRenderHome
 * above: that function's own 2-argument contract is diagram-pinned
 * (homeRouteGate.test.ts's existing cases) and answers a still-true, still-
 * scoped question ("should Home render given the EXISTING arc's step +
 * corpus") — reshaping it to know about a phase it has no other reason to
 * know about would blur its one job. This is a pure OR-composition instead:
 * showingFrontDoorCards is server-truth-and-session-state ALREADY resolved
 * by useFrontDoorGate (lib/useFrontDoorGate.ts, wired to
 * shouldShowFrontDoorCards in lib/frontDoorCards.ts) — this function never
 * re-derives eligibility itself (FE renders served truth), it only composes
 * the two already-honest answers into the one the "/" route needs.
 */
export function shouldRenderHomeWithFrontDoor(
  showingFrontDoorCards: boolean,
  step: OnboardingStep,
  hasRealCorpus: boolean,
): boolean {
  if (showingFrontDoorCards) return false;
  return shouldRenderHome(step, hasRealCorpus);
}
